import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

type UseInfiniteScrollOptions = {
  hasMore: boolean;
  loading: boolean;
  fetchMore: () => void;
  rootMargin?: string;
};

type UseInfiniteScroll<T> = {
  triggerRef: RefObject<T>;
};

const DEFAULT_PX_TO_TRIGGER_SHOW_MORE = '50px';

export const useInfiniteScroll = <T extends Element>({
  hasMore,
  loading,
  fetchMore,
  rootMargin = DEFAULT_PX_TO_TRIGGER_SHOW_MORE,
}: UseInfiniteScrollOptions): UseInfiniteScroll<T> => {
  const triggerRef = useRef<T>(null);

  useEffect(() => {
    const trigger = triggerRef.current;

    if (!trigger || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchMore();
        }
      },
      { rootMargin }
    );

    observer.observe(trigger);

    return () => observer.disconnect();
  }, [hasMore, loading, fetchMore, rootMargin]);

  return { triggerRef };
};
